/**
 * <listicle-progress>
 *
 * Reading-progress track in the masthead's fixed strip. On scroll (passive,
 * throttled to one update per animation frame) it measures how far the page
 * has been read and sets the width of its [data-ls-fill] child as a
 * percentage. aria-valuenow only changes in 5% steps so screen readers are
 * not flooded while the page scrolls; it is only maintained when the element
 * was rendered with role="progressbar" (a blank label makes it decorative).
 *
 * Listeners hang off one AbortController aborted in disconnectedCallback so
 * Theme Editor re-renders (shopify:section:load / unload) never leak. While
 * the section is selected in the editor the bar parks at ~2/3 so the merchant
 * can see what they are styling.
 */

const PREVIEW_PROGRESS = 0.66;
const ARIA_STEP = 5;

class ListicleProgress extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.fill = this.querySelector('[data-ls-fill]');
    this.ticking = false;
    this.preview = false;
    this.lastPct = -1;

    const schedule = () => {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };

    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    window.addEventListener('pageshow', schedule, { signal });

    // Lazy images and app embeds change the document height after load;
    // re-measure so the ratio stays honest without waiting for the next scroll.
    if ('ResizeObserver' in window && document.body) {
      this.resizeObserver = new ResizeObserver(schedule);
      this.resizeObserver.observe(document.body);
    }

    document.addEventListener(
      'shopify:section:select',
      (e) => {
        if (e.target && e.target.contains(this)) this.setPreview(true);
      },
      { signal }
    );
    document.addEventListener(
      'shopify:section:deselect',
      (e) => {
        if (e.target && e.target.contains(this)) this.setPreview(false);
      },
      { signal }
    );

    this.update();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  setPreview(on) {
    this.preview = on;
    this.update();
  }

  measure() {
    const doc = document.documentElement;
    const max = doc.scrollHeight - window.innerHeight;
    if (max <= 0) return 0;
    const y = window.scrollY || doc.scrollTop || 0;
    return Math.min(1, Math.max(0, y / max));
  }

  update() {
    const progress = this.preview ? PREVIEW_PROGRESS : this.measure();
    const width = (progress * 100).toFixed(2) + '%';
    if (this.fill) this.fill.style.width = width;
    else this.style.setProperty('--ls-progress', progress.toFixed(4));

    if (this.getAttribute('role') !== 'progressbar') return;
    const pct = Math.round((progress * 100) / ARIA_STEP) * ARIA_STEP;
    if (pct !== this.lastPct) {
      this.lastPct = pct;
      this.setAttribute('aria-valuenow', String(pct));
    }
  }
}

if (!customElements.get('listicle-progress')) {
  customElements.define('listicle-progress', ListicleProgress);
}
